import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { faqs } from '@/mocks/categories';

function BrandSection() {
  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="container-main">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-4">关于虫虫漫画网</h1>
          <p className="text-foreground-600 leading-relaxed">
            虫虫漫画网成立于2023年，是一个专注于韩国漫画大全免费在线阅读的专业平台。我们致力于为广大漫画爱好者提供最优质的韩漫阅读体验，支持独特的<strong>下拉式阅读模式</strong>，让读者无需频繁点击翻页，只需轻松滑动即可享受行云流水般的阅读旅程。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            {
              icon: 'ri-book-open-line',
              title: '海量漫画资源',
              desc: '收录数千部优质韩漫作品，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情等多种类型，满足不同读者的阅读偏好。',
            },
            {
              icon: 'ri-shield-check-line',
              title: '安全可靠',
              desc: '所有漫画内容均为合法授权或正版转载，平台严格遵守版权法规，让读者放心阅读，作者安心创作。',
            },
            {
              icon: 'ri-smartphone-line',
              title: '全设备适配',
              desc: '采用响应式设计，完美支持电脑、平板和手机。下拉式阅读在移动端的触屏体验尤为出色。',
            },
          ].map((item) => (
            <div key={item.title} className="p-6 rounded-xl bg-background-100 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <i className={`${item.icon} text-2xl text-primary-500`}></i>
              </div>
              <h3 className="text-lg font-heading text-foreground-950 mb-2">{item.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-12 md:py-16 bg-background-950">
      <div className="container-main">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: '2000+', label: '收录漫画' },
            { value: '500万+', label: '累计读者' },
            { value: '99.2%', label: '好评率' },
            { value: '365天', label: '全年无休更新' },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-foreground-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="container-main">
        <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">我们的优势</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {[
            {
              title: '下拉式阅读体验',
              desc: '告别传统翻页模式，虫虫漫画网首创韩漫下拉式阅读功能。读者只需向下滚动即可连续阅读，无需中断，阅读更加流畅自然。这是我们对漫画阅读方式的一次革新。',
              icon: 'ri-scroll-to-bottom-line',
            },
            {
              title: '完全免费阅读',
              desc: '虫虫漫画网上的绝大多数漫画均为免费阅读。我们通过广告维持运营，确保读者无需支付任何费用即可畅享高质量韩漫内容。让好内容触手可及，是我们的初心。',
              icon: 'ri-money-cny-circle-line',
            },
            {
              title: '每日持续更新',
              desc: '我们的编辑团队与多家韩国漫画平台保持紧密合作，确保热门连载作品能够第一时间同步更新。每周有超过100话新内容上线，让读者永不书荒。',
              icon: 'ri-refresh-line',
            },
            {
              title: '智能推荐系统',
              desc: '基于读者的阅读历史和偏好，我们的智能推荐系统可以为每位用户精准推荐可能感兴趣的漫画作品，帮助读者发现更多优质内容。',
              icon: 'ri-robot-line',
            },
            {
              title: '高清画质享受',
              desc: '所有漫画图片均采用高清无损压缩技术，在保证加载速度的同时，为读者提供细腻清晰的画质体验。每一格漫画的细节都清晰可见。',
              icon: 'ri-hd-line',
            },
            {
              title: '社区互动交流',
              desc: '读者可以在每部漫画下方发表评论和感想，与其他同好交流讨论。虫虫漫画网不仅是一个阅读平台，更是一个充满活力的漫画爱好者社区。',
              icon: 'ri-chat-smile-2-line',
            },
          ].map((feature) => (
            <div key={feature.title} className="flex gap-4 p-5 rounded-xl bg-background-50">
              <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className={`${feature.icon} text-xl text-accent-600`}></i>
              </div>
              <div>
                <h3 className="text-base font-heading text-foreground-950 mb-1.5">{feature.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="container-main">
        <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">常见问题</h2>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <details key={index} className="group rounded-xl bg-background-100 overflow-hidden">
              <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
              </summary>
              <div className="px-6 pb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pd1hosenbniolest5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (!response.ok || (parsed?.code && parsed.code !== 'OK') || (serverMsg && serverMsg.includes('spam'))) {
        throw new Error(serverMsg || '提交失败，请稍后重试');
      }

      setFormStatus('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '提交失败，请稍后重试';
      setFormError(msg);
      setFormStatus('error');
    }
  };

  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="container-main">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-4">联系我们</h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            如果您有任何问题、建议或合作意向，请通过以下表单与我们联系。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@chongchongmanhua.com' },
              { icon: 'ri-wechat-line', title: '微信公众号', value: '虫虫漫画网' },
              { icon: 'ri-weibo-line', title: '官方微博', value: '@虫虫漫画网' },
            ].map((item) => (
              <div key={item.title} className="p-4 rounded-xl bg-background-50 text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className={`${item.icon} text-lg text-primary-500`}></i>
                </div>
                <h3 className="text-xs font-medium text-foreground-500 mb-1">{item.title}</h3>
                <p className="text-sm text-foreground-800">{item.value}</p>
              </div>
            ))}
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form=""
            className="space-y-4 bg-background-50 p-6 rounded-xl"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact_name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="contact_email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                主题 <span className="text-primary-500">*</span>
              </label>
              <input
                type="text"
                id="contact_subject"
                name="subject"
                required
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                placeholder="请输入主题"
              />
            </div>

            <div>
              <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="contact_message"
                name="message"
                required
                rows={5}
                maxLength={500}
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                placeholder="请输入您的留言内容（最多500字）"
              ></textarea>
            </div>

            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />

            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="px-8 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>
              {formStatus === 'success' && (
                <span className="text-sm text-green-600 font-medium">
                  <i className="ri-check-line mr-1"></i> 留言已发送，我们会尽快回复！
                </span>
              )}
              {formStatus === 'error' && (
                <span className="text-sm text-primary-600 font-medium">{formError}</span>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于虫虫漫画网',
    description: '虫虫漫画网是韩国漫画大全免费在线阅读平台，提供韩漫下拉式阅读体验，海量优质韩漫资源每日更新。',
    url: 'https://www.chongchongmanhua.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解虫虫漫画网 - 韩国漫画大全免费在线阅读平台。品牌介绍、平台优势与联系方式，提供优质韩漫下拉式阅读体验。"
        keywords="虫虫漫画网,关于我们,韩国漫画,韩漫,免费漫画平台"
        canonicalUrl="https://www.chongchongmanhua.com/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="container-main">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>
        <BrandSection />
        <StatsSection />
        <FeaturesSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}